/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import {
  Platform,
  TouchableOpacity,
  StyleProp,
  ViewStyle,
  View,
} from "react-native";

import { Text } from "UpduoLibrary";
import {
  colorWithLuminance,
  colorWithOpacity,
  skaleGray,
  skaleWhite,
  buttonDisabledDefault,
  skaleCircleGray,
  upduoPrimaryBlue,
} from "config/UpduoColors";

interface ButtonProps {
  width?: number | string;
  delayButton?: boolean;
  marginTop?: number;
  marginHorizontal?: number | undefined;
  marginLeft?: number;
  marginRight?: number;
  padding?: number;
  height?: number | string;
  textColor?: string;
  hoverTextColor?: string;
  disabledTextColor?: string;
  borderRadius?: number;
  fontSize?: number;
  text?: string;
  color?: string;
  hoverColor?: string;
  disabledColor?: string;
  animatedColors?: string[];
  borderColor?: string;
  disabledBorderColor?: string;
  borderWidthDisabled?: number;
  borderWidth?: number;
  isFlashing?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  content?: React.ReactNode;
  letterSpacing?: number;
  animateOnHover?: boolean;
  hoverBorderWidth?: number;
}

export default function Button({
  width = 200,
  delayButton = false,
  marginTop = 0,
  marginHorizontal,
  marginLeft,
  marginRight,
  height = 50,
  textColor = skaleWhite,
  disabledTextColor = skaleCircleGray,
  borderRadius = 25,
  fontSize = 16,
  text = "",
  color = upduoPrimaryBlue,
  hoverTextColor = color === skaleWhite
    ? textColor
    : colorWithLuminance(textColor, 0.2),
  hoverColor = color === skaleWhite
    ? colorWithOpacity(skaleGray, 0.2)
    : colorWithLuminance(color, 0.2),
  disabledColor = delayButton ? color : buttonDisabledDefault,
  borderColor = skaleCircleGray,
  disabledBorderColor = delayButton ? color : skaleCircleGray,
  borderWidthDisabled = delayButton ? 0 : 1,
  borderWidth = 0,
  isFlashing,
  disabled,
  onPress,
  content,
  letterSpacing = fontSize / 10,
  animateOnHover = true,
  hoverBorderWidth = borderWidth,
}: ButtonProps) {
  const [isHovering, setIsHovering] = useState(false);
  const isAnimatedGradient = isFlashing && !disabled;

  function checkColorDisabledOrHover(disabledStyle, hoverStyle, noHoverStyle) {
    return disabled ? disabledStyle : isHovering ? hoverStyle : noHoverStyle;
  }

  const buttonStyle: StyleProp<ViewStyle> = {
    height,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: checkColorDisabledOrHover(
      borderWidthDisabled,
      hoverBorderWidth,
      borderWidth
    ),
    borderColor: disabled ? disabledBorderColor : borderColor,
    borderRadius,
  };
  if (!isAnimatedGradient) {
    buttonStyle.backgroundColor = checkColorDisabledOrHover(
      disabledColor,
      hoverColor,
      color
    );
  }
  const button = (
    <TouchableOpacity
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            if (animateOnHover) {
              setIsHovering(true);
            }
          },
          onMouseLeave: () => {
            setIsHovering(false);
          },
        },
      })}
      disabled={disabled}
      onPress={onPress}
      style={buttonStyle}
    >
      {content ? (
        content
      ) : (
        <View style={{ flexDirection: "row" }}>
          <Text
            style={{
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing,
              color: checkColorDisabledOrHover(
                disabledTextColor,
                hoverTextColor,
                textColor
              ),
            }}
            isHeading={true}
          >
            {text}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
  return (
    <View
      style={{
        marginTop,
        marginHorizontal,
        marginLeft,
        marginRight,
        alignSelf: marginHorizontal ? "stretch" : undefined,
        width: marginHorizontal ? undefined : width,
        overflow: "hidden",
      }}
    >
      {isAnimatedGradient ? (
        <div className={"buttonBlueAnimation"}>{button}</div>
      ) : (
        button
      )}
    </View>
  );
}
